import api from './api';

// ============================================
// Trial Balance Types - أنواع ميزان المراجعة
// ============================================

export interface TrialBalanceFilter {
  fromDate?: string;
  toDate?: string;
  fiscalPeriodId?: number;
  accountType?: string;
  accountLevel?: number;
  includeZeroBalances?: boolean;
  showOpeningBalance?: boolean;
  onlyFinalAccounts?: boolean;
  branchId?: number;
  costCenterId?: number;
}

export interface TrialBalanceDto {
  accountId: number;
  accountCode: string;
  accountName: string;
  accountNameAr: string;
  accountType: string;
  accountNature: string;
  accountLevel: number;
  parentAccountId?: number;
  parentAccountCode?: string;
  openingDebit: number;
  openingCredit: number;
  periodDebit: number;
  periodCredit: number;
  totalDebit: number;
  totalCredit: number;
  closingDebit: number;
  closingCredit: number;
  balance: number;
}

export interface TrialBalanceSummaryDto {
  totalOpeningDebit: number;
  totalOpeningCredit: number;
  totalPeriodDebit: number;
  totalPeriodCredit: number;
  totalDebit: number;
  totalCredit: number;
  totalClosingDebit: number;
  totalClosingCredit: number;
  isBalanced: boolean;
  difference: number;
  totalAccounts: number;
  accountsWithBalance: number;
}

export interface TrialBalanceReportResult {
  data: TrialBalanceDto[];
  summary: TrialBalanceSummaryDto;
  fromDate?: string;
  toDate?: string;
  fiscalPeriodName?: string;
  generatedAt: string;
}

// ============================================
// General Ledger Types - أنواع دفتر الأستاذ
// ============================================

export interface GeneralLedgerFilter {
  accountId?: number;
  fromDate?: string;
  toDate?: string;
  fiscalPeriodId?: number;
  branchId?: number;
  costCenterId?: number;
  includeOpeningBalance?: boolean;
  pageNumber?: number;
  pageSize?: number;
}

export interface GeneralLedgerDto {
  ledgerId: number;
  accountId: number;
  accountCode: string;
  accountName: string;
  accountNameAr: string;
  transactionDate: string;
  journalEntryId: number;
  journalEntryNumber: string;
  description?: string;
  sourceType?: string;
  sourceId?: number;
  debitAmount: number;
  creditAmount: number;
  runningBalance: number;
  costCenterName?: string;
  branchName?: string;
}

export interface AccountStatementResult {
  accountId: number;
  accountCode: string;
  accountName: string;
  accountNameAr: string;
  openingBalance: number;
  totalDebit: number;
  totalCredit: number;
  closingBalance: number;
  transactions: GeneralLedgerDto[];
  fromDate?: string;
  toDate?: string;
  totalRecords: number;
}

// ============================================
// Financial Reports Service
// ============================================

export const financialReportsService = {
  // Trial Balance - ميزان المراجعة
  getTrialBalance: async (filter: TrialBalanceFilter): Promise<TrialBalanceReportResult> => {
    const params = new URLSearchParams();
    if (filter.fromDate) params.append('fromDate', filter.fromDate);
    if (filter.toDate) params.append('toDate', filter.toDate);
    if (filter.fiscalPeriodId) params.append('fiscalPeriodId', filter.fiscalPeriodId.toString());
    if (filter.accountType) params.append('accountType', filter.accountType);
    if (filter.accountLevel) params.append('accountLevel', filter.accountLevel.toString());
    if (filter.includeZeroBalances !== undefined) params.append('includeZeroBalances', filter.includeZeroBalances.toString());
    if (filter.showOpeningBalance !== undefined) params.append('showOpeningBalance', filter.showOpeningBalance.toString());
    if (filter.onlyFinalAccounts !== undefined) params.append('onlyFinalAccounts', filter.onlyFinalAccounts.toString());
    if (filter.branchId) params.append('branchId', filter.branchId.toString());
    if (filter.costCenterId) params.append('costCenterId', filter.costCenterId.toString());

    const response = await api.get(`/financial-reports/trial-balance?${params.toString()}`);
    return response.data;
  },

  getTrialBalanceByFiscalPeriod: async (fiscalPeriodId: number, includeZeroBalances: boolean = false): Promise<TrialBalanceReportResult> => {
    const response = await api.get(`/financial-reports/trial-balance/fiscal-period/${fiscalPeriodId}?includeZeroBalances=${includeZeroBalances}`);
    return response.data;
  },

  getTrialBalanceSummary: async (filter: TrialBalanceFilter): Promise<TrialBalanceSummaryDto> => {
    const params = new URLSearchParams();
    if (filter.fromDate) params.append('fromDate', filter.fromDate);
    if (filter.toDate) params.append('toDate', filter.toDate);
    if (filter.fiscalPeriodId) params.append('fiscalPeriodId', filter.fiscalPeriodId.toString());

    const response = await api.get(`/financial-reports/trial-balance/summary?${params.toString()}`);
    return response.data;
  },

  // General Ledger - دفتر الأستاذ
  getGeneralLedger: async (filter: GeneralLedgerFilter): Promise<AccountStatementResult> => {
    const params = new URLSearchParams();
    if (filter.accountId) params.append('accountId', filter.accountId.toString());
    if (filter.fromDate) params.append('fromDate', filter.fromDate);
    if (filter.toDate) params.append('toDate', filter.toDate);
    if (filter.fiscalPeriodId) params.append('fiscalPeriodId', filter.fiscalPeriodId.toString());
    if (filter.branchId) params.append('branchId', filter.branchId.toString());
    if (filter.costCenterId) params.append('costCenterId', filter.costCenterId.toString());
    if (filter.includeOpeningBalance !== undefined) params.append('includeOpeningBalance', filter.includeOpeningBalance.toString());
    if (filter.pageNumber) params.append('pageNumber', filter.pageNumber.toString());
    if (filter.pageSize) params.append('pageSize', filter.pageSize.toString());

    const response = await api.get(`/financial-reports/general-ledger?${params.toString()}`);
    return response.data;
  },

  // Account Statement - كشف حساب
  getAccountStatement: async (accountId: number, fromDate?: string, toDate?: string): Promise<AccountStatementResult> => {
    const params = new URLSearchParams();
    if (fromDate) params.append('fromDate', fromDate);
    if (toDate) params.append('toDate', toDate);

    const response = await api.get(`/financial-reports/account-statement/${accountId}?${params.toString()}`);
    return response.data;
  },
};

export default financialReportsService;

// ============================================
// Financial Statements Types - أنواع القوائم المالية
// ============================================

export interface FinancialStatementFilter {
  fiscalPeriodId?: number;
  fromDate?: string;
  toDate?: string;
  branchId?: number;
  costCenterId?: number;
  showComparison?: boolean;
  comparisonFiscalPeriodId?: number;
}

export interface IncomeStatementLineDto {
  accountId: number;
  accountCode: string;
  accountName: string;
  accountNameAr: string;
  accountLevel: number;
  parentAccountId?: number;
  lineType: string;
  currentPeriodAmount: number;
  previousPeriodAmount: number;
  comparisonPeriodAmount: number;
  variance: number;
  variancePercentage: number;
  isHeader: boolean;
  isTotal: boolean;
  displayOrder: number;
}

export interface IncomeStatementResult {
  // 1. Revenue Section
  revenueLines: IncomeStatementLineDto[];
  totalRevenue: number;
  
  // 2. COGS Section
  cogsLines: IncomeStatementLineDto[];
  totalCOGS: number;
  
  // 3. Gross Profit
  grossProfit: number;
  
  // 4. Operating Expenses
  operatingExpenseLines: IncomeStatementLineDto[];
  totalOperatingExpenses: number;
  
  // 5. Operating Income
  operatingIncome: number;
  
  // 6. Other Income
  otherIncomeLines: IncomeStatementLineDto[];
  totalOtherIncome: number;
  
  // 7. Other Expenses
  otherExpenseLines: IncomeStatementLineDto[];
  totalOtherExpenses: number;
  
  // 8. Income Before Tax
  incomeBeforeTax: number;
  
  // 9. Tax Expense
  taxExpense: number;
  
  // 10. Net Income
  netIncome: number;
  
  // Legacy/Compatibility Fields
  expenseLines: IncomeStatementLineDto[];
  totalExpenses: number;
  operatingExpenses: number;
  otherIncome: number;
  otherExpenses: number;
  
  // Comparison
  comparisonNetIncome: number;
  netIncomeVariance: number;
  netIncomeVariancePercentage: number;
  
  // Meta
  fromDate?: string;
  toDate?: string;
  fiscalPeriodName?: string;
  generatedAt: string;
}

export interface BalanceSheetLineDto {
  accountId: number;
  accountCode: string;
  accountName: string;
  accountNameAr: string;
  accountLevel: number;
  parentAccountId?: number;
  section: string;
  subSection: string;
  currentPeriodBalance: number;
  comparisonPeriodBalance: number;
  variance: number;
  variancePercentage: number;
  isHeader: boolean;
  isSubTotal: boolean;
  isTotal: boolean;
  displayOrder: number;
}

export interface BalanceSheetResult {
  currentAssets: BalanceSheetLineDto[];
  fixedAssets: BalanceSheetLineDto[];
  otherAssets: BalanceSheetLineDto[];
  totalCurrentAssets: number;
  totalFixedAssets: number;
  totalOtherAssets: number;
  totalAssets: number;
  currentLiabilities: BalanceSheetLineDto[];
  longTermLiabilities: BalanceSheetLineDto[];
  totalCurrentLiabilities: number;
  totalLongTermLiabilities: number;
  totalLiabilities: number;
  equityItems: BalanceSheetLineDto[];
  totalEquity: number;
  retainedEarnings: number;
  currentYearNetIncome: number;
  totalLiabilitiesAndEquity: number;
  isBalanced: boolean;
  difference: number;
  asOfDate?: string;
  fiscalPeriodName?: string;
  generatedAt: string;
}

export interface CashFlowLineDto {
  description: string;
  descriptionAr: string;
  category: string;
  amount: number;
  comparisonAmount: number;
  isSubTotal: boolean;
  isTotal: boolean;
  displayOrder: number;
}

export interface CashFlowStatementResult {
  operatingActivities: CashFlowLineDto[];
  netCashFromOperating: number;
  investingActivities: CashFlowLineDto[];
  netCashFromInvesting: number;
  financingActivities: CashFlowLineDto[];
  netCashFromFinancing: number;
  netChangeInCash: number;
  beginningCashBalance: number;
  endingCashBalance: number;
  fromDate?: string;
  toDate?: string;
  fiscalPeriodName?: string;
  generatedAt: string;
}

// Add to financialReportsService
export const financialStatementsService = {
  // Income Statement - قائمة الدخل
  getIncomeStatement: async (filter: FinancialStatementFilter): Promise<IncomeStatementResult> => {
    const params = new URLSearchParams();
    if (filter.fiscalPeriodId) params.append('fiscalPeriodId', filter.fiscalPeriodId.toString());
    if (filter.fromDate) params.append('fromDate', filter.fromDate);
    if (filter.toDate) params.append('toDate', filter.toDate);
    if (filter.branchId) params.append('branchId', filter.branchId.toString());
    const response = await api.get(`/financial-reports/income-statement?${params.toString()}`);
    return response.data;
  },

  // Balance Sheet - الميزانية العمومية
  getBalanceSheet: async (filter: FinancialStatementFilter): Promise<BalanceSheetResult> => {
    const params = new URLSearchParams();
    if (filter.fiscalPeriodId) params.append('fiscalPeriodId', filter.fiscalPeriodId.toString());
    if (filter.toDate) params.append('toDate', filter.toDate);
    if (filter.branchId) params.append('branchId', filter.branchId.toString());
    const response = await api.get(`/financial-reports/balance-sheet?${params.toString()}`);
    return response.data;
  },

  // Cash Flow Statement - قائمة التدفقات النقدية
  getCashFlowStatement: async (filter: FinancialStatementFilter): Promise<CashFlowStatementResult> => {
    const params = new URLSearchParams();
    if (filter.fiscalPeriodId) params.append('fiscalPeriodId', filter.fiscalPeriodId.toString());
    if (filter.fromDate) params.append('fromDate', filter.fromDate);
    if (filter.toDate) params.append('toDate', filter.toDate);
    const response = await api.get(`/financial-reports/cash-flow?${params.toString()}`);
    return response.data;
  },
};